/**
 * 학습자 언어에 맞는 표시 텍스트 선택 유틸.
 *
 * 학습진행설정v2에서 관리자가 입력하는 ko/en 자유 텍스트(컴포넌트 label,
 * Guide message)를 학습자 화면에서 i18n.language 에 맞춰 고른다.
 * 빈 문자열('')은 "없는 값"으로 취급한다.
 *
 * Spec: docs/superpowers/specs/2026-05-26-config-v2-learner-english-labels-design.md
 */

export interface LabeledComponent {
  label?: string | null
  labelEn?: string | null
  type: string
  /** config-v2 컴포넌트 옵션(video source 등). semantic fallback 판별용. */
  options?: Record<string, unknown> | null
}

/** semantic 라벨이 필요한 컴포넌트의 fallback 문구(i18n). 명시 label이 없을 때만 사용. */
export interface ComponentLabelFallbacks {
  /** 반짝깨처 — 직전 레슨 flash(summary) 영상. */
  flashQueture?: string
}

/** video subtype:flash + contentScope:previous = 반짝깨처(직전 레슨 요약 영상). */
function isFlashPreviousVideo(comp: LabeledComponent): boolean {
  if (comp.type !== 'video') return false
  const source = (comp.options as { source?: { subtype?: string; contentScope?: string } } | null | undefined)?.source
  return source?.subtype === 'flash' && source?.contentScope === 'previous'
}

/**
 * en: labelEn → label → (semantic) → type. 그 외(ko): label → (semantic) → type.
 * 명시 label(관리자 입력)이 우선. 없을 때만 semantic fallback(예: 반짝깨처) 적용.
 */
export function pickComponentLabel(
  comp: LabeledComponent,
  lang: string,
  fallbacks?: ComponentLabelFallbacks,
): string {
  const explicit = lang.startsWith('en') ? comp.labelEn || comp.label : comp.label
  if (explicit) return explicit
  if (fallbacks?.flashQueture && isFlashPreviousVideo(comp)) return fallbacks.flashQueture
  return comp.type
}

/** en: en → ko → null. 그 외(ko): ko → null. */
export function pickLocalizedText(
  ko: string | null | undefined,
  en: string | null | undefined,
  lang: string,
): string | null {
  if (lang.startsWith('en')) return en || ko || null
  return ko || null
}
